import React, { useRef, useState } from "react";
import { PageWrapper } from "../material-ledger-summary";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { StyledButton } from "../../../../shared/components/buttons/custom-button";
import useNotification from "../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../store/transaction-certificates.store";
import { MLAppConfig } from "../../ml-models/MLAppConfig";
import { useApi } from "../../../../shared/hooks/useApi";

const ACCEPTED_TYPES = ".xlsx,.xls";
const MAX_SIZE_MB = 10;
// Official Textile Exchange FPF template (Knowledge Center). Opened in a new tab
// on demand so the user never leaves the upload form.
const FPF_TEMPLATE_URL =
  "https://textileexchange.org/knowledge-center/documents/farm-production-form/";

interface AllowedFacility {
  originFacilityTeId: string;
  country: string;
  state: string;
}
interface LotIssues {
  lotNo: string;
  issues: string[];
}
/** Structured SC-validation failure (origin facility / country-state mismatches). */
interface ScErrorDetail {
  summary?: string;
  scNo?: string;
  generalIssues?: string[];
  errorsByLot?: LotIssues[];
  allowedFacilities?: AllowedFacility[];
}
interface FpfErrorLogEntry {
  timestamp: string;
  fileName: string;
  fileSizeKB: string;
  stage: "client-validation" | "server-validation" | "network";
  errors: string[];          // flat list (schema/format errors + flattened SC errors)
  sc?: ScErrorDetail;        // present when the failure is an SC-relationship mismatch
}

/** Split the backend's "err1; err2 | Row 15: err3" strings into list items. */
const splitServerErrors = (raw: string): string[] =>
  raw
    .split(/\s*\|\s*|;\s*(?=[A-Z(])/g)
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Normalise the backend error body into flat lines + optional structured SC data.
 * Handles three shapes: the structured SC response (errorsByLot/allowedFacilities),
 * a plain {response|message} string, and a {errors:[...]} list.
 */
const parseServerError = (data: any): { errors: string[]; sc?: ScErrorDetail } => {
  if (data && (Array.isArray(data.errorsByLot) || Array.isArray(data.allowedFacilities))) {
    const sc: ScErrorDetail = {
      summary: data.response || data.summary,
      scNo: data.scNo,
      generalIssues: data.generalIssues || [],
      errorsByLot: data.errorsByLot || [],
      allowedFacilities: data.allowedFacilities || [],
    };
    const flat: string[] = [...(sc.generalIssues || [])];
    (sc.errorsByLot || []).forEach((lot) =>
      lot.issues.forEach((iss) => flat.push(`Lot '${lot.lotNo}': ${iss}`))
    );
    return { errors: flat.length ? flat : [sc.summary || "SC validation failed."], sc };
  }
  if (data && Array.isArray(data.errors)) return { errors: data.errors };
  const msg: string = data?.response || data?.message || "Upload failed. Please check the file and try again.";
  return { errors: splitServerErrors(String(msg)) };
};

const FpfForm: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [errorLog, setErrorLog] = useState<FpfErrorLogEntry[]>([]);
  // After a failed upload the user must Clear or choose a new file before retrying
  const [uploadBlocked, setUploadBlocked] = useState(false);
  // Persistent success banner (in addition to the toast), shown under the upload area
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  // Drag-over highlight for the drop zone
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { cbLicensingCode } = useTransactionCertificateStore();
  const { notify } = useNotification();
  const { executePut } = useApi();

  const latestEntry = errorLog.length > 0 ? errorLog[0] : null;
  // The latest error is shown prominently only while it is still "active"
  // (uploadBlocked). Once the user clears it — by choosing a new file or pressing
  // Clear — it is demoted into the "Previous attempts" history instead of lingering
  // as a current validation error.
  const showActiveError = uploadBlocked && !!latestEntry;
  const previousEntries = showActiveError ? errorLog.slice(1) : errorLog;

  const pushErrorLog = (
    stage: FpfErrorLogEntry["stage"],
    errors: string[],
    file: File | null,
    sc?: ScErrorDetail
  ) => {
    setErrorLog((prev) => [
      {
        timestamp: new Date().toISOString(),
        fileName: file?.name ?? "(no file)",
        fileSizeKB: file ? (file.size / 1024).toFixed(1) : "0",
        stage,
        errors,
        sc,
      },
      ...prev,
    ]);
  };

  // Build + download an error log for the given entries. Pass the whole errorLog
  // for the full log, or a single [entry] for one attempt. The attempt number is
  // derived from the entry's position in the full log so it stays consistent.
  const downloadErrorLog = (entries: FpfErrorLogEntry[], suffix = "") => {
    const lines: string[] = [
      "FPF UPLOAD — ERROR LOG",
      `Generated : ${new Date().toLocaleString()}`,
      `CB Code   : ${cbLicensingCode || "(unknown)"}`,
      "=".repeat(70),
      "",
    ];
    entries.forEach((entry) => {
      const attemptNo = errorLog.length - errorLog.indexOf(entry);
      lines.push(
        `[Attempt ${attemptNo}] ${new Date(entry.timestamp).toLocaleString()}`,
        `File  : ${entry.fileName} (${entry.fileSizeKB} KB)`,
        `Stage : ${entry.stage}`,
        "Errors:"
      );
      entry.errors.forEach((err, j) => lines.push(`  ${j + 1}. ${err}`));
      if (entry.sc?.allowedFacilities?.length) {
        lines.push("", "Allowed on this SC (origin facility / country / state):");
        entry.sc.allowedFacilities.forEach((f) =>
          lines.push(`  - ${f.originFacilityTeId}  ${f.country}/${f.state}`)
        );
      }
      lines.push("-".repeat(70), "");
    });

    const blob = new Blob([lines.join("\r\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    a.download = `fpf-upload-errors${suffix ? `-${suffix}` : ""}-${stamp}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadErrorLog = () => downloadErrorLog(errorLog);

  // Shared client-side validation used by both the file picker and drag-and-drop.
  const acceptFile = (file: File | null) => {
    setUploadBlocked(false); // a new file selection lifts the error block
    setSuccessMsg(null);     // and clears any prior success banner
    if (!file) return;

    const errors: string[] = [];
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      errors.push("Only .xlsx and .xls files are accepted.");
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      errors.push(`File size must be under ${MAX_SIZE_MB} MB.`);
    }

    if (errors.length > 0) {
      pushErrorLog("client-validation", errors, file);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setSelectedFile(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    acceptFile(e.target.files?.[0] ?? null);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (uploading) return;
    acceptFile(e.dataTransfer.files?.[0] ?? null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      notify("Please select a file first.", "error");
      return;
    }
    if (!cbLicensingCode) {
      notify("CB Licensing Code not available. Please log in again.", "error");
      return;
    }

    setUploading(true);

    try {
      const fileBytes = await selectedFile.arrayBuffer();
      const fpfBase = process.env.REACT_APP_FPF_USE_LOCAL === "true"
        ? process.env.REACT_APP_FPF_LOCAL_ML_BASE ?? ""
        : "";
      const url = `${fpfBase}${MLAppConfig.UPLOAD_FPF_EXCEL}?filename=${encodeURIComponent(selectedFile.name)}&tcCbLicensingCode=${encodeURIComponent(cbLicensingCode)}`;

      const response = await executePut(url, fileBytes, {
        headers: { "Content-Type": "application/octet-stream" },
      });

      if (response?.status === 200) {
        const okMsg = (response?.data as any)?.response
          || "FPF file uploaded successfully. Processing has started.";
        notify(okMsg, "success");
        setSuccessMsg(okMsg); // persistent banner under the upload area
        setErrorLog([]); // success — clear any stale validation errors from prior attempts
        handleReset();   // clear the form so the user can upload another
        return;
      }

      // useApi returns {data, status} for 400/500 instead of throwing.
      const { errors, sc } = parseServerError(response?.data);
      pushErrorLog("server-validation", errors, selectedFile, sc);
      setUploadBlocked(true);
      notify("Upload failed. See validation errors below.", "error");
    } catch (err: any) {
      const errData = err?.response?.data;
      if (errData && (Array.isArray(errData.errorsByLot) || Array.isArray(errData.errors) || errData.response)) {
        const { errors, sc } = parseServerError(errData);
        pushErrorLog("server-validation", errors, selectedFile, sc);
      } else {
        pushErrorLog("network", [err?.message || "Network error. Could not reach the FPF upload service."], selectedFile);
      }
      setUploadBlocked(true);
      notify("Upload failed. See errors below.", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadBlocked(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <PageWrapper>
      <Typography mt={4} variant="h6" gutterBottom align="center" fontWeight="600">
        Farm Production Form - File Upload
      </Typography>

      <Paper
        elevation={2}
        sx={{ mt: 3, mx: "auto", maxWidth: 720, p: { xs: 2.5, sm: 4 }, borderRadius: 3 }}
      >
        <Typography variant="body2" mb={2} color="text.secondary" align="center">
          Upload the completed FPF Excel template (.xlsx / .xls). It will be validated
          and processed automatically once uploaded.
        </Typography>

        {/* Get the official blank template — opens in a new tab (no navigation away) */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mb: 2 }}>
          <StyledButton
            variant="outlined"
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={() => window.open(FPF_TEMPLATE_URL, "_blank", "noopener,noreferrer")}
          >
            Download sample template
          </StyledButton>
          <Tooltip
            arrow
            placement="right"
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography variant="caption" display="block" fontWeight={600} mb={0.5}>
                  Official Textile Exchange FPF template
                </Typography>
                <Typography variant="caption" display="block">
                  Opens the Textile Exchange Knowledge Center in a new tab. Download the
                  blank Farm Production Form, fill it in, then rename the file to your FPF
                  Number and upload it here.
                </Typography>
              </Box>
            }
          >
            <InfoOutlinedIcon
              fontSize="small"
              sx={{ color: "text.secondary", cursor: "help" }}
              aria-label="About the FPF sample template"
            />
          </Tooltip>
        </Box>

        {/* Interactive drag-and-drop / click-to-browse zone */}
        <Box
          onClick={() => !uploading && fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (!uploading) setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          sx={{
            p: 4,
            border: "2px dashed",
            borderColor: isDragging ? "primary.main" : "#ccc",
            borderRadius: 2,
            backgroundColor: isDragging ? "rgba(25,118,210,0.06)" : "#fafafa",
            textAlign: "center",
            cursor: uploading ? "default" : "pointer",
            transition: "border-color .2s, background-color .2s",
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            onChange={handleFileChange}
            // Reset the value every time the picker opens so re-selecting the SAME
            // (now corrected) file still fires onChange — without this the browser
            // skips the event for an identical selection and the button/error state
            // never refreshes. stopPropagation keeps the hidden input's own click
            // (we trigger programmatically) from re-bubbling to the zone handler.
            onClick={(e) => {
              (e.target as HTMLInputElement).value = "";
              e.stopPropagation();
            }}
            style={{ display: "none" }}
            id="fpf-file-input"
          />

          <CloudUploadOutlinedIcon sx={{ fontSize: 46, color: "text.secondary", mb: 1 }} />
          <Typography variant="body1" fontWeight={600} gutterBottom>
            Drag &amp; drop your FPF Excel file here
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" color="text.secondary">or</Typography>
            <StyledButton
              variant="contained"
              disabled={uploading}
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              Browse Files
            </StyledButton>
            <Tooltip
              arrow
              placement="right"
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="caption" display="block" fontWeight={600} mb={0.5}>
                    File name must match the FPF Number
                  </Typography>
                  <Typography variant="caption" display="block">
                    Format: <strong>FPF-&lt;CB&gt;-&lt;CO&nbsp;TE-ID&gt;-&lt;suffix&gt;.xlsx</strong>
                  </Typography>
                  <Typography variant="caption" display="block">
                    Example: <strong>FPF-CUC-12345678-20260708.xlsx</strong>
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.85 }}>
                    The name (without the extension) must equal the FPF Number inside the file. Matching is case-insensitive.
                  </Typography>
                </Box>
              }
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ color: "text.secondary", cursor: "help" }}
                aria-label="FPF file name format help"
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </Box>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Accepts .xlsx / .xls files
          </Typography>

          {selectedFile && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
              <Chip
                icon={<InsertDriveFileOutlinedIcon />}
                label={`${selectedFile.name}  (${(selectedFile.size / 1024).toFixed(1)} KB)`}
                onDelete={uploading ? undefined : handleReset}
                variant="outlined"
                sx={{
                  maxWidth: "100%",
                  height: "auto",
                  py: 0.5,
                  "& .MuiChip-label": { whiteSpace: "normal", wordBreak: "break-all" },
                }}
              />
            </Box>
          )}
        </Box>

        {selectedFile && !uploading && (
          <Box sx={{ display: "flex", gap: 2, mt: 3, alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
            <StyledButton variant="contained" onClick={handleUpload} disabled={uploadBlocked}>
              Upload &amp; Submit
            </StyledButton>
            <StyledButton variant="outlined" onClick={handleReset}>
              Clear
            </StyledButton>
            {uploadBlocked && (
              <Typography variant="caption" color="error">
                Fix the errors below, then Clear or choose a corrected file to upload again.
              </Typography>
            )}
          </Box>
        )}

        {uploading && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Validating and uploading…
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {successMsg && !uploading && (
          <Alert severity="success" sx={{ mt: 3 }} onClose={() => setSuccessMsg(null)}>
            <AlertTitle>Upload successful</AlertTitle>
            {successMsg}
          </Alert>
        )}

        {showActiveError && latestEntry && (
          <Alert
            severity="error"
            sx={{ mt: 3, "& .MuiAlert-message": { width: "100%" } }}
            action={
              <Tooltip arrow title="Download error log">
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={handleDownloadErrorLog}
                  aria-label="Download error log"
                >
                  <FileDownloadOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          >
            <AlertTitle>Validation errors ({latestEntry.errors.length})</AlertTitle>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              {new Date(latestEntry.timestamp).toLocaleString()} — {latestEntry.fileName} ({latestEntry.fileSizeKB} KB) — {latestEntry.stage}
            </Typography>

            {latestEntry.sc ? (
              <>
                {latestEntry.sc.summary && (
                  <Typography variant="body2" color="error" mb={1.5}>
                    {latestEntry.sc.summary}
                  </Typography>
                )}

                {!!latestEntry.sc.generalIssues?.length && (
                  <Box sx={{ mb: 1.5 }}>
                    {latestEntry.sc.generalIssues.map((g, i) => (
                      <Typography key={i} variant="body2" color="error">• {g}</Typography>
                    ))}
                  </Box>
                )}

                {latestEntry.sc.errorsByLot?.map((lot, i) => (
                  <Box key={i} sx={{ mb: 1.5 }}>
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      Lot: {lot.lotNo}
                    </Typography>
                    {lot.issues.map((iss, j) => (
                      <Typography key={j} variant="body2" color="error" sx={{ pl: 1.5 }}>
                        • {iss}
                      </Typography>
                    ))}
                  </Box>
                ))}

                {!!latestEntry.sc.allowedFacilities?.length && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight={600} color="text.primary" mb={0.5}>
                      Allowed on this SC{latestEntry.sc.scNo ? ` (${latestEntry.sc.scNo})` : ""}:
                    </Typography>
                    <TableContainer sx={{ maxWidth: 460, border: "1px solid #e0e0e0", borderRadius: 1, backgroundColor: "#fff" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Origin Facility TE-ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>State/Province</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {latestEntry.sc.allowedFacilities.map((f, i) => (
                            <TableRow key={i}>
                              <TableCell>{f.originFacilityTeId}</TableCell>
                              <TableCell>{f.country}</TableCell>
                              <TableCell>{f.state}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </>
            ) : (
              latestEntry.errors.map((err, i) => (
                <Typography key={i} variant="body2" color="error">
                  • {err}
                </Typography>
              ))
            )}
          </Alert>
        )}

        {previousEntries.length > 0 && (
          <Accordion sx={{ mt: 2, backgroundColor: "transparent" }} elevation={0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" color="text.secondary">
                Previous attempts ({previousEntries.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {previousEntries.map((entry, i) => (
                <Box key={i} sx={{ mb: 2, pb: 1, borderBottom: "1px solid #eee" }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(entry.timestamp).toLocaleString()} — {entry.fileName} ({entry.fileSizeKB} KB) — {entry.stage}
                    </Typography>
                    <Tooltip arrow title="Download this attempt's log">
                      <IconButton
                        size="small"
                        onClick={() => downloadErrorLog([entry], "attempt")}
                        aria-label="Download this attempt's log"
                      >
                        <FileDownloadOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {entry.errors.map((err, j) => (
                    <Typography key={j} variant="body2" color="error">
                      • {err}
                    </Typography>
                  ))}
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Always-visible one-line file-naming hint. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 3 }}>
          <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
          <Typography variant="caption" color="text.secondary">
            File naming format for the template to be uploaded:{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
              FPF_&lt;CB_Code&gt;_&lt;FPF_Number&gt;.xlsx
            </Box>
          </Typography>
        </Box>
      </Paper>
    </PageWrapper>
  );
};

export default FpfForm;
